import React, { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { Avatar, Typography, Button } from "@mui/material";
import { GridColumns } from "@mui/x-data-grid/models/colDef/gridColDef";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import {
  DataGrid,
  GridRenderCellParams,
  GridFilterItem,
} from "@mui/x-data-grid";
// import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

import { IStore } from "../../../../index";
import { defaultPageSize } from "../../../util/constants";
import ModuleHelper from "../../../util/ModuleHelper";
import { useGlobalStyles } from "../../../util/ThemePalette";
import UserService from "../../../services/User.service";
import PaperWrapper from "../../paper/PaperWrapper";
import { IAdvisorStatistic } from "../../../models/statistics";
import CustomHeader from "../../custom_grid_header/CustomHeader";
// import { UserRole } from "../../../common/Enums";

interface IProps {
  userService: UserService;
}

const AdvisorStatistics = (props: IProps): React.ReactElement => {
  // const reviews = "Reviews";
  const classes = useGlobalStyles();

  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [pending, setPending] = useState<boolean>(false);
  const [statistics, setStatistics] = useState<IAdvisorStatistic[]>([]);
  const [filter, setFilter] = useState<GridFilterItem[]>([]);
  const [showResetFilter, setShowResetFilter] = useState<boolean>(false);
  // const [columns, setColumns] = useState<GridColumns>([]);

  const dealerContext = useSelector(
    (store: IStore) => store.auth.user.dealerContext,
    /* (store: IStore) => ({
      context: store.auth.user.dealerContext,
      config: store.main.dealershipConfig,
    }), */
    shallowEqual
  );

  useEffect(() => {
    setPending(true);
    props.userService
      .getAdvisorStatistics()
      .then((response) => {
        setStatistics(response);
      })
      .catch((error) => ModuleHelper.grow.showError(error))
      .finally(() => setPending(false));

    /* const tempColumns =
      dealerContext.config?.tipWhoReceive &&
      dealerContext.config?.tipWhoReceive.includes(UserRole.ADVISOR)
        ? [...defaultColumns, ...rateAndTipColumns]
        : defaultColumns;

    setColumns(tempColumns);
    }, [dealerContext.config, dealerContext.context]); */
  }, [dealerContext]);

  /* const moneyCellRender = (
    params: GridRenderCellParams
  ): React.ReactElement => (
    <React.Fragment>
      <AttachMoneyIcon sx={{ fontSize: 15 }} color={"primary"} />
      <Typography>{params.value}</Typography>
    </React.Fragment>
  );*/

  const handleFilterChange = (e: any) => {
    setFilter(e.items);
    e.items[0].value ? setShowResetFilter(true) : setShowResetFilter(false);
  };

  const handleFilterReset = (e: any): void => {
    e.stopPropagation();
    setFilter([]);
    setShowResetFilter(false);
  };

  /*const defaultColumns: GridColumns = [ */
  const columns: GridColumns = [
    {
      field: "name",
      headerName: "Name",
      minWidth: 200,
      flex: 1,
      renderCell: (params: GridRenderCellParams<IAdvisorStatistic>) => {
        return (
          <section style={{ display: "flex" }}>
            <Avatar
              src={`${params.row?.employee.avatarURL}`}
              alt={params.row?.employee.firstName}
            />
            <Typography
              style={{ marginTop: 12, marginLeft: 5, whiteSpace: "initial" }}
            >
              {params.row?.employee.firstName} {params.row?.employee.lastName}
            </Typography>
          </section>
        );
      },
      headerAlign: "center",
      valueGetter: (e) =>
        e.row.employee.firstName + " " + e.row.employee.lastName,
      sortComparator: (v1, v2) => v1.localeCompare(v2),
      renderHeader: (params) => (
        <CustomHeader
          params={params}
          reset={handleFilterReset}
          filter={filter}
        />
      ),
    },
    {
      field: "relatedToOpenRo",
      headerName: "Open RO's",
      minWidth: 150,
      flex: 1,
      type: "number",
      align: "center",
      headerAlign: "center",
      renderHeader: (params) => (
        <CustomHeader
          params={params}
          reset={handleFilterReset}
          filter={filter}
        />
      ),
    },
    {
      field: "recommendedWorksApprovedByAdvisor",
      headerName: "Approved By Advisor",
      minWidth: 180,
      flex: 1,
      type: "number",
      align: "center",
      headerAlign: "center",
      renderHeader: (params) => (
        <CustomHeader
          params={params}
          reset={handleFilterReset}
          filter={filter}
        />
      ),
    },
    {
      field: "recommendedWorksApprovedByCustomer",
      headerName: "Approved By Customer",
      minWidth: 190,
      flex: 1,
      type: "number",
      align: "center",
      headerAlign: "center",
      renderHeader: (params) => (
        <CustomHeader
          params={params}
          reset={handleFilterReset}
          filter={filter}
        />
      ),
    },
    {
      field: "recommendedWorksDeclinedByCustomer",
      headerName: "Declined By Customer",
      minWidth: 190,
      flex: 1,
      type: "number",
      align: "center",
      headerAlign: "center",
      renderHeader: (params) => (
        <CustomHeader
          params={params}
          reset={handleFilterReset}
          filter={filter}
        />
      ),
    },
    {
      field: "recommendedWorksPendingCustomerResponse",
      headerName: "Pending Customer",
      minWidth: 190,
      flex: 1,
      type: "number",
      align: "center",
      headerAlign: "center",
      renderHeader: (params) => (
        <CustomHeader
          params={params}
          reset={handleFilterReset}
          filter={filter}
        />
      ),
    },
    {
      field: "recommendedWorksSuccessRate",
      headerName: "Success Rate",
      minWidth: 190,
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderHeader: (params) => (
        <CustomHeader
          params={params}
          reset={handleFilterReset}
          filter={filter}
        />
      ),
    },
  ];

  /* const rateAndTipColumns: GridColumns = [
    {
      field: "averageScore",
      headerName: "Average Score",
      description: `${reviews} (Average Score)`,
      minWidth: 130,
      flex: 1,
      type: "number",
      align: "center",
      headerAlign: "center",
    },
    {
      field: "totalTipAmount",
      headerName: "Total Tip",
      description: `Total Tip Amount`,
      minWidth: 130,
      flex: 1,
      type: "number",
      align: "center",
      headerAlign: "center",
      renderCell: moneyCellRender,
    },
  ]; */

  const render = (): React.ReactElement => {
    return (
      <PaperWrapper
        header={"ADVISORS"}
        rightHeader={
          <Button
            variant={"contained"}
            startIcon={<FilterAltOffIcon />}
            onClick={handleFilterReset}
            style={{ display: showResetFilter ? "flex" : "none" }}
          >
            Reset
          </Button>
        }
      >
        <DataGrid
          rows={statistics}
          columns={columns}
          loading={pending}
          autoHeight={true}
          pageSize={pageSize}
          className={classes.grid}
          onPageSizeChange={setPageSize}
          onFilterModelChange={handleFilterChange}
          filterModel={{ items: filter }}
        />
      </PaperWrapper>
    );
  };

  return render();
};

export default AdvisorStatistics;
